import { reduce, unnest } from 'ramda'

import type { FoodMode } from 'app/dataTypes/foodModes'

import { Allowance } from '../../../_common/useReportAllowances'

const minCellWidth = 100
const nameColumnWidth = 200

type Options = {
  showPersonal?: boolean
  showCommon?: boolean
}

const defaultOptions: Options = {
  showPersonal: true,
  showCommon: true,
}

const getOptionsWithDefaults = (options: Options = defaultOptions) => {
  return { ...defaultOptions, ...options }
}

export enum ColumnKey {
  Org = 'org',
  OrgGhost = 'orgGhost', // пустая ячейка
  Personal = 'personal',
  Common = 'common',
  Total = 'total',
  Breakfast = 'breakfast', // Завтрак
  Lunch = 'lunch', // Обед
  Afternoon = 'afternoon', // Полдник
}

const createDinamicColumnKey = (allowance: Allowance, foodMode: FoodMode) => {
  return `${ColumnKey.Personal}_${allowance}_${foodMode}`
}

export const createFoodModesColumnKeys = (allowance: Allowance) => {
  return [ColumnKey.Breakfast, ColumnKey.Lunch, ColumnKey.Afternoon].map((foodMode) =>
    createDinamicColumnKey(allowance, foodMode),
  )
}

export const columnKeysLeft = [ColumnKey.Org]
export const columnKeysLeftHeaders = [columnKeysLeft, [ColumnKey.OrgGhost], [ColumnKey.OrgGhost]]
export const columnKeysLeftHeadersNoSpan = [columnKeysLeft]

export const createColumnKeysMain = (allowances: Array<Allowance>, options?: Options) => {
  const { showCommon, showPersonal } = getOptionsWithDefaults(options)
  return [
    ...(showPersonal ? unnest(allowances.map(createFoodModesColumnKeys)) : []),
    ...(showCommon ? [ColumnKey.Common] : []),
    ColumnKey.Total,
  ]
}

export const createColumnKeysMainHeaders = (allowances: Array<string>, options?: Options) => {
  const { showCommon, showPersonal } = getOptionsWithDefaults(options)
  if (showPersonal) {
    return [
      [ColumnKey.Personal, ...(showCommon ? [ColumnKey.Common] : []), ColumnKey.Total],
      allowances,
      unnest(allowances.map(createFoodModesColumnKeys)),
    ]
  }
  return [[...(showCommon ? [ColumnKey.Common] : []), ColumnKey.Total]]
}

const headerCells = {
  [ColumnKey.Org]: 'Наименование заведения',
  [ColumnKey.OrgGhost]: '',
  [ColumnKey.Personal]: 'Индив. справки',
  [ColumnKey.Common]: 'Общие справки',
  [ColumnKey.Total]: 'Всего',
}

export const createHeaderCells = (allowances: Array<Allowance>) => {
  return reduce(
    (acc, allowance) => {
      const key1 = createDinamicColumnKey(allowance, ColumnKey.Breakfast)
      const key2 = createDinamicColumnKey(allowance, ColumnKey.Lunch)
      const key3 = createDinamicColumnKey(allowance, ColumnKey.Afternoon)
      acc[allowance] = allowance
      acc[key1] = 'Завтрак'
      acc[key2] = 'Обед'
      acc[key3] = 'Полдник'
      return acc
    },
    headerCells,
    allowances,
  )
}

const headerCellsProps = {
  [ColumnKey.Org]: { alignLeft: true, width: nameColumnWidth, rowSpan: 3 },
  [ColumnKey.OrgGhost]: { alignLeft: true, width: 0, stripPadding: true },
  [ColumnKey.Common]: { alignRight: true, rowSpan: 3, borderRight: true, minWidth: minCellWidth },
  [ColumnKey.Total]: { alignRight: true, rowSpan: 3, minWidth: minCellWidth },
}

const headerCellsPropsNoSpan = {
  [ColumnKey.Org]: { alignLeft: true, width: nameColumnWidth },
  [ColumnKey.OrgGhost]: { alignLeft: true, width: 0, stripPadding: true },
  [ColumnKey.Common]: { alignRight: true, borderRight: true, minWidth: minCellWidth },
  [ColumnKey.Total]: { alignRight: true, minWidth: minCellWidth },
}

export const createHeaderCellsProps = (allowances: Array<Allowance>, options?: Options) => {
  const { showPersonal } = getOptionsWithDefaults(options)
  if (!showPersonal) {
    return headerCellsPropsNoSpan
  }

  let index = 0
  return {
    [ColumnKey.Personal]: { alignCenter: true, colSpan: allowances.length * 3, borderRight: true },
    ...reduce(
      (acc, allowance) => {
        const key1 = createDinamicColumnKey(allowance, ColumnKey.Breakfast)
        const key2 = createDinamicColumnKey(allowance, ColumnKey.Lunch)
        const key3 = createDinamicColumnKey(allowance, ColumnKey.Afternoon)

        acc[allowance] = { alignCenter: true, borderRight: true, colSpan: 3 }
        acc[key1] = { alignRight: true, minWidth: minCellWidth }
        acc[key2] = { alignRight: true, minWidth: minCellWidth }
        acc[key3] = { alignRight: true, borderRight: true, minWidth: minCellWidth }
        if (index === 0) {
          acc[allowance] = {
            alignCenter: true,
            borderRight: true,
            ignoreFirstChild: true,
            colSpan: 3,
          }
          acc[key1] = { alignRight: true, ignoreFirstChild: true, minWidth: minCellWidth }
          acc[key2] = { alignRight: true, minWidth: minCellWidth }
          acc[key3] = { alignRight: true, borderRight: true, minWidth: minCellWidth }
        }
        if (index === allowances.length - 1) {
          acc[allowance] = {
            alignCenter: true,
            ignoreLastChild: true,
            borderRight: true,
            colSpan: 3,
          }
          acc[key1] = { alignRight: true, minWidth: minCellWidth }
          acc[key2] = { alignRight: true, minWidth: minCellWidth }
          acc[key3] = {
            alignRight: true,
            ignoreLastChild: true,
            borderRight: true,
            minWidth: minCellWidth,
          }
        }
        index += 1
        return acc
      },
      headerCellsProps,
      allowances,
    ),
  }
}

const cellsProps = {
  [ColumnKey.Org]: { alignLeft: true, width: nameColumnWidth },
  [ColumnKey.Common]: { alignRight: true, borderRight: true, minWidth: minCellWidth },
  [ColumnKey.Total]: { alignRight: true, minWidth: minCellWidth },
}

export const createCellsProps = (allowances: Array<Allowance>) => {
  let index = 0
  return reduce(
    (acc, allowance) => {
      const key1 = createDinamicColumnKey(allowance, ColumnKey.Breakfast)
      const key2 = createDinamicColumnKey(allowance, ColumnKey.Lunch)
      const key3 = createDinamicColumnKey(allowance, ColumnKey.Afternoon)

      acc[key1] = { alignRight: true, minWidth: minCellWidth }
      acc[key2] = { alignRight: true, minWidth: minCellWidth }
      acc[key3] = { alignRight: true, borderRight: true, minWidth: minCellWidth }
      if (index === 0) {
        acc[key1] = { alignRight: true, ignoreFirstChild: true, minWidth: minCellWidth }
      }
      index += 1
      return acc
    },
    cellsProps,
    allowances,
  )
}

const totalCellsProps = {
  [ColumnKey.Org]: { alignRight: true, width: nameColumnWidth, bold: true },
  [ColumnKey.Common]: {
    alignRight: true,
    highlighted: true,
    bold: true,
    borderRight: true,
    minWidth: minCellWidth,
  },
  [ColumnKey.Total]: { alignRight: true, highlighted: true, bold: true, minWidth: minCellWidth },
}

export const createTotalCellsProps = (allowanceIDs: Array<string>) => {
  let index = 0
  return reduce(
    (acc, allowance) => {
      const key1 = createDinamicColumnKey(allowance, ColumnKey.Breakfast)
      const key2 = createDinamicColumnKey(allowance, ColumnKey.Lunch)
      const key3 = createDinamicColumnKey(allowance, ColumnKey.Afternoon)

      acc[key1] = { alignRight: true, highlighted: true, bold: true, minWidth: minCellWidth }
      acc[key2] = { alignRight: true, highlighted: true, bold: true, minWidth: minCellWidth }
      acc[key3] = {
        alignRight: true,
        highlighted: true,
        bold: true,
        borderRight: true,
        minWidth: minCellWidth,
      }
      if (index === 0) {
        acc[key1] = {
          alignRight: true,
          highlighted: true,
          bold: true,
          ignoreFirstChild: true,
          minWidth: minCellWidth,
        }
      }
      index += 1
      return acc
    },
    totalCellsProps,
    allowanceIDs,
  )
}
